import DateRange from "components/date-range";
import Filter from "components/filters";
import { InputMulti } from "components/input-multi";
import { SelectBox } from "components/uikit-adapter";
import {
  type ComponentType,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Col as ColGrid, Row as RowGrid } from "react-flexbox-grid";
import intl from "react-intl-universal";
import {
  getAllBusinessUnitsService,
  getAllFamiliesService,
  getAllLabeledFamiliesService,
  getAllMaterialGroupsService,
  getAllMaterialsService,
  getAllStatusService,
  getAllSuppliersService,
} from "services/optimizations/optimizations.service";
import type {
  BusinessUnitOption,
  FamilyOption,
  FilterOptimizationProps,
  LabeledFamilyOption,
  MaterialGroupOption,
  MaterialOption,
  StatusOption,
  SupplierOption,
  VolumeOptimizationFilter,
} from "types/optimizations";
import { formatErrorMessage } from "utils/handle-error";

// react-flexbox-grid types don't declare children; cast once here to keep the JSX clean
type GridRowProps = { children?: ReactNode };
type GridColProps = { children?: ReactNode; xs?: number; lg?: number; md?: number };
const Row = RowGrid as ComponentType<GridRowProps>;
const Col = ColGrid as ComponentType<GridColProps>;

const EMPTY_PARAMS: VolumeOptimizationFilter = {
  OrderVolumeCodes: [],
  SourceIds: [],
  DestinationIds: [],
  MaterialGroupIds: [],
  MaterialNumbers: [],
  StepId: null,
  StatusId: null,
  FamilyIds: [],
  LabelFamilyIds: [],
  PickupDateFrom: "",
  PickupDateTo: "",
  DeliveryDateFrom: "",
  DeliveryDateTo: "",
  UpdatedDateFrom: "",
  UpdatedDateTo: "",
  HistoryView: false,
};

const FilterOptimization = ({
  initialData,
  showFilter,
  handleClose,
  triggerClose,
  showHistory = false,
}: FilterOptimizationProps) => {
  const [rangeInvalidCollection, setRangeInvalidCollection] = useState(false);
  const [rangeInvalidDelivery, setRangeInvalidDelivery] = useState(false);
  const [rangeInvalidUpdate, setRangeInvalidUpdate] = useState(false);

  const [orderVolumeCodes, setOrderVolumeCodes] = useState<string[]>([]);
  const [sourceIds, setSourceIds] = useState<SupplierOption[]>([]);
  const [destinationIds, setDestinationIds] = useState<BusinessUnitOption[]>([]);
  const [materialGroupIds, setMaterialGroupIds] = useState<MaterialGroupOption[]>([]);
  const [familyIds, setFamilyIds] = useState<FamilyOption[]>([]);
  const [labelFamilyIds, setLabelFamilyIds] = useState<LabeledFamilyOption[]>([]);
  const [materialNumbers, setMaterialNumbers] = useState<MaterialOption[]>([]);
  const [statusId, setStatusId] = useState<number | null>(null);
  const [pickupDateFrom, setPickupDateFrom] = useState("");
  const [pickupDateTo, setPickupDateTo] = useState("");
  const [deliveryDateFrom, setDeliveryDateFrom] = useState("");
  const [deliveryDateTo, setDeliveryDateTo] = useState("");
  const [updatedDateFrom, setUpdatedDateFrom] = useState("");
  const [updatedDateTo, setUpdatedDateTo] = useState("");

  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnitOption[]>([]);
  const [materialGroups, setMaterialGroups] = useState<MaterialGroupOption[]>([]);
  const [materials, setMaterials] = useState<MaterialOption[]>([]);
  const [status, setStatus] = useState<StatusOption[]>([]);
  const [families, setFamilies] = useState<FamilyOption[]>([]);
  const [labeledFamilies, setLabeledFamilies] = useState<LabeledFamilyOption[]>([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const fetchOptionsAsync = useCallback(async () => {
    const fetchOneAsync = async <T,>(service: () => Promise<T[]>, setter: (data: T[]) => void) => {
      try {
        const data = await service();
        setter(data);
      } catch (error) {
        console.log("Erro encontrado", formatErrorMessage(error));
      }
    };

    await Promise.all([
      fetchOneAsync<SupplierOption>(getAllSuppliersService, setSuppliers),
      fetchOneAsync<BusinessUnitOption>(getAllBusinessUnitsService, setBusinessUnits),
      fetchOneAsync<MaterialGroupOption>(getAllMaterialGroupsService, setMaterialGroups),
      fetchOneAsync<MaterialOption>(getAllMaterialsService, setMaterials),
      fetchOneAsync<StatusOption>(getAllStatusService, setStatus),
      fetchOneAsync<FamilyOption>(getAllFamiliesService, setFamilies),
      fetchOneAsync<LabeledFamilyOption>(getAllLabeledFamiliesService, setLabeledFamilies),
    ]);
    setOptionsLoaded(true);
  }, []);

  useEffect(() => {
    fetchOptionsAsync();
  }, [fetchOptionsAsync]);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!showFilter) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current || !initialData || !optionsLoaded) return;

    initializedRef.current = true;

    setOrderVolumeCodes(initialData.OrderVolumeCodes.map(String));
    setStatusId(initialData.StatusId);
    setSourceIds(suppliers.filter((s) => initialData.SourceIds.includes(s.IdFornecedor)));
    setDestinationIds(
      businessUnits.filter((bu) => initialData.DestinationIds.includes(bu.IdUnidadeNegocio))
    );
    setMaterialGroupIds(
      materialGroups.filter((mg) => (initialData.MaterialGroupIds ?? []).includes(mg.Id))
    );
    setMaterialNumbers(materials.filter((m) => initialData.MaterialNumbers.includes(m.Id)));
    setFamilyIds(families.filter((f) => initialData.FamilyIds.includes(f.Id)));
    setLabelFamilyIds(labeledFamilies.filter((lf) => initialData.LabelFamilyIds.includes(lf.Id)));
    setPickupDateFrom(initialData.PickupDateFrom);
    setPickupDateTo(initialData.PickupDateTo);
    setDeliveryDateFrom(initialData.DeliveryDateFrom);
    setDeliveryDateTo(initialData.DeliveryDateTo);
    setUpdatedDateFrom(initialData.UpdatedDateFrom);
    setUpdatedDateTo(initialData.UpdatedDateTo);
  }, [
    showFilter,
    initialData,
    optionsLoaded,
    suppliers,
    businessUnits,
    materialGroups,
    materials,
    families,
    labeledFamilies,
  ]);

  const handleCloseSearch = () => {
    handleClose({
      OrderVolumeCodes: orderVolumeCodes.map(Number).filter(Boolean),
      SourceIds: sourceIds.map((s) => s.IdFornecedor),
      DestinationIds: destinationIds.map((bu) => bu.IdUnidadeNegocio),
      MaterialGroupIds: materialGroupIds.map((mg) => mg.Id),
      MaterialNumbers: materialNumbers.map((m) => m.Id),
      StepId: null,
      StatusId: statusId,
      FamilyIds: familyIds.map((f) => f.Id),
      LabelFamilyIds: labelFamilyIds.map((lf) => lf.Id),
      PickupDateFrom: pickupDateFrom,
      PickupDateTo: pickupDateTo,
      DeliveryDateFrom: deliveryDateFrom,
      DeliveryDateTo: deliveryDateTo,
      UpdatedDateFrom: updatedDateFrom,
      UpdatedDateTo: updatedDateTo,
      HistoryView: showHistory,
    });
  };

  const handleCleanClick = () => {
    setOrderVolumeCodes([]);
    setSourceIds([]);
    setDestinationIds([]);
    setMaterialGroupIds([]);
    setFamilyIds([]);
    setLabelFamilyIds([]);
    setMaterialNumbers([]);
    setStatusId(null);
    setPickupDateFrom("");
    setPickupDateTo("");
    setDeliveryDateFrom("");
    setDeliveryDateTo("");
    setUpdatedDateFrom("");
    setUpdatedDateTo("");
    setRangeInvalidCollection(false);
    setRangeInvalidDelivery(false);
    setRangeInvalidUpdate(false);
    handleClose({ ...EMPTY_PARAMS, HistoryView: showHistory });
  };

  const isValidRanges = () => rangeInvalidCollection || rangeInvalidDelivery || rangeInvalidUpdate;

  return (
    <Filter
      showFilter={showFilter}
      handleCleanClick={handleCleanClick}
      handleCloseSearch={handleCloseSearch}
      triggerClose={triggerClose}
      disabledSearch={isValidRanges()}
    >
      <Row>
        <Col xs={12} lg={12} md={12}>
          <InputMulti
            key={orderVolumeCodes.join(",")}
            labelText={intl.get("loadOptimization.orderCodeVolume")}
            value={orderVolumeCodes}
            onChange={(value: string[]) => setOrderVolumeCodes(value)}
          />
        </Col>
      </Row>
      {showHistory && (
        <Row>
          <Col xs={12} lg={12} md={12}>
            <SelectBox
              name="status"
              label={intl.get("commons.status")}
              value={statusId}
              options={status}
              valueKey="Id"
              labelKey="Descricao"
              onChange={(value: StatusOption) => {
                setStatusId(value === null ? null : value.Id);
              }}
              placeholder={intl.get("commons.status")}
              searchable={true}
              openOnFocus
              clearable
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={12} lg={12} md={12}>
          <SelectBox
            name="source"
            label={intl.get("commons.source")}
            placeholder={intl.get("commons.source")}
            valueKey="IdFornecedor"
            labelKey="Descricao"
            value={sourceIds}
            options={suppliers}
            onChange={(value: SupplierOption[]) => setSourceIds(value ?? [])}
            searchable
            openOnFocus
            clearable
            multi
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={12} md={12}>
          <SelectBox
            name="destination"
            label={intl.get("commons.destiny")}
            placeholder={intl.get("commons.destiny")}
            valueKey="IdUnidadeNegocio"
            labelKey="Descricao"
            value={destinationIds}
            options={businessUnits}
            onChange={(value: BusinessUnitOption[]) => setDestinationIds(value ?? [])}
            searchable
            openOnFocus
            clearable
            multi
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={12} md={12}>
          <SelectBox
            name="materialGroup"
            label={intl.get("commons.wallet")}
            placeholder={intl.get("commons.wallet")}
            valueKey="Id"
            labelKey="Description"
            value={materialGroupIds}
            options={materialGroups}
            onChange={(value: MaterialGroupOption[]) => setMaterialGroupIds(value ?? [])}
            searchable
            openOnFocus
            clearable
            multi
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={12} md={12}>
          <SelectBox
            name="family"
            label={intl.get("commons.family")}
            value={familyIds}
            options={families}
            valueKey="Id"
            labelKey="Name"
            onChange={(value: FamilyOption[]) => setFamilyIds(value ?? [])}
            placeholder={intl.get("commons.family")}
            searchable
            openOnFocus
            clearable
            multi
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={12} md={12}>
          <SelectBox
            name="labeledFamily"
            label={intl.get("commons.labeledFamily")}
            value={labelFamilyIds}
            options={labeledFamilies}
            valueKey="Id"
            labelKey="Description"
            onChange={(value: LabeledFamilyOption[]) => setLabelFamilyIds(value ?? [])}
            placeholder={intl.get("commons.labeledFamily")}
            searchable
            openOnFocus
            clearable
            multi
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={12} md={12}>
          <SelectBox
            name="material"
            label={intl.get("commons.material")}
            placeholder={intl.get("commons.material")}
            valueKey="Id"
            labelKey="Description"
            value={materialNumbers}
            options={materials}
            onChange={(value: MaterialOption[]) => setMaterialNumbers(value ?? [])}
            searchable
            openOnFocus
            clearable
            multi
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={12} md={12}>
          <DateRange
            labelDe={intl.get("commons.collectDateDE")}
            labelAte={intl.get("commons.collectDateAT")}
            dataDe={pickupDateFrom}
            dataAte={pickupDateTo}
            onChangeDataDe={(value: string) => setPickupDateFrom(value)}
            onChangeDataAte={(value: string) => setPickupDateTo(value)}
            onRangeIsValid={(value: boolean) => setRangeInvalidCollection(value)}
          />
          <DateRange
            labelDe={intl.get("loadOptimization.filter.deliveryDateOf")}
            labelAte={intl.get("loadOptimization.filter.deliveryDateBy")}
            dataDe={deliveryDateFrom}
            dataAte={deliveryDateTo}
            onChangeDataDe={(value: string) => setDeliveryDateFrom(value)}
            onChangeDataAte={(value: string) => setDeliveryDateTo(value)}
            onRangeIsValid={(value: boolean) => setRangeInvalidDelivery(value)}
          />
        </Col>
      </Row>
      {showHistory && (
        <Row>
          <Col xs={12} lg={12} md={12}>
            <DateRange
              labelDe={intl.get("loadOptimization.filter.updateDateOf")}
              labelAte={intl.get("loadOptimization.filter.updateDateUntil")}
              dataDe={updatedDateFrom}
              dataAte={updatedDateTo}
              onChangeDataDe={(value: string) => setUpdatedDateFrom(value)}
              onChangeDataAte={(value: string) => setUpdatedDateTo(value)}
              onRangeIsValid={(value: boolean) => setRangeInvalidUpdate(value)}
            />
          </Col>
        </Row>
      )}
    </Filter>
  );
};

export default FilterOptimization;
